import './coapplicantRelationshipPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { RelationshipEnum } from '../../../store/coapplicantStore/enums/RelationshipEnum';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { isCoapplicantPersisted },
    coapplicantRelationship,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantRelationship,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      coapplicantRelationshipValue: coapplicantRelationship,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<RelationshipEnum>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }
    const coapplicantRelationshipValue = event.target.value;

    setIsInitialSelection(
      isEmpty(coapplicantRelationship) && isValidClickEvent(event),
    );

    setCoapplicantRelationship(coapplicantRelationshipValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { coapplicantRelationshipValue } = values;

    if (!coapplicantRelationshipValue) {
      return;
    }

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page coapplicant-relationship-page">
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantRelationship.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantRelationship.form.h1}>
                {t('CoapplicantRelationshipPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantRelationship.form.h2}>
                {t('CoapplicantRelationshipPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantRelationship.alert.error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  name="coapplicantRelationshipValue"
                  options={[
                    {
                      label: t('CoapplicantRelationshipPage.spouse'),
                      value: RelationshipEnum.SPOUSE,
                      testId:
                        locator.pages.coapplicantRelationship.form.radioOption1,
                    },
                    {
                      label: t('CoapplicantRelationshipPage.relative'),
                      value: RelationshipEnum.RELATIVE,
                      testId:
                        locator.pages.coapplicantRelationship.form.radioOption2,
                    },
                    {
                      label: t('CoapplicantRelationshipPage.friend'),
                      value: RelationshipEnum.FRIEND,
                      testId:
                        locator.pages.coapplicantRelationship.form.radioOption3,
                    },
                    {
                      label: t('CoapplicantRelationshipPage.coworker'),
                      value: RelationshipEnum.COWORKER,
                      testId:
                        locator.pages.coapplicantRelationship.form.radioOption4,
                    },
                    {
                      label: t('CoapplicantRelationshipPage.other'),
                      value: RelationshipEnum.OTHER,
                      testId:
                        locator.pages.coapplicantRelationship.form.radioOption5,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantRelationship.form.radioGroupRoot
                  }
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantRelationship.form.submitButton
                  }
                  disabled={isEmpty(coapplicantRelationship)}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantRelationshipPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantRelationship.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantRelationshipPage';
